import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '@bliks-io/ui';
import {
  CreatePartnerMemberRequestSchema,
  isPartnerUserType,
  type CreatePartnerMemberRequest,
  type User
} from '@bliks-io/contracts';
import { FormInput, FormSelect } from '@/components/form/rhf';
import { getUserTypeOptions } from '@/lib/user-utils';

interface PartnerMemberDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  mode: 'create' | 'edit';
  /** The member being edited (ignored in create mode). */
  member?: User | null;
  isSubmitting?: boolean;
  onSubmit: (values: CreatePartnerMemberRequest) => void;
}

/**
 * Add/edit a partner team member. Partner tenants are kept simple: just name,
 * email, an optional position, and the partner role (Admin/Staff) — no
 * departments or Stellen. Email + position are create-only (the email is the
 * identity, the position is captured at invite).
 */
export function PartnerMemberDialog({
  open,
  onOpenChange,
  ...formProps
}: PartnerMemberDialogProps) {
  const { t } = useTranslation();
  const isCreate = formProps.mode === 'create';

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {t(isCreate ? 'partner.team.dialog.createTitle' : 'partner.team.dialog.editTitle')}
          </DialogTitle>
          <DialogDescription>{t('partner.team.dialog.description')}</DialogDescription>
        </DialogHeader>

        {/* Radix unmounts DialogContent's children while closed, so the form is
            a fresh instance on every open — no reset effect and no remount key
            at the call site. */}
        <PartnerMemberForm onOpenChange={onOpenChange} {...formProps} />
      </DialogContent>
    </Dialog>
  );
}

function PartnerMemberForm({
  onOpenChange,
  mode,
  member,
  isSubmitting,
  onSubmit
}: Omit<PartnerMemberDialogProps, 'open'>) {
  const { t } = useTranslation();
  const isCreate = mode === 'create';

  const form = useForm<CreatePartnerMemberRequest>({
    resolver: zodResolver(CreatePartnerMemberRequestSchema),
    mode: 'onSubmit',
    reValidateMode: 'onChange',
    defaultValues: {
      firstName: member?.firstName ?? '',
      lastName: member?.lastName ?? '',
      email: member?.email ?? '',
      jobTitle: member?.jobTitle ?? '',
      userType: member?.userTypes.find(isPartnerUserType) ?? 'partner_staff'
    }
  });

  // The partner tenant's own branch — same source the superadmin form reads,
  // so a third partner role or a renamed label lands in both at once.
  const roleOptions = getUserTypeOptions(t, 'partner');

  return (
    <form
      noValidate
      onSubmit={form.handleSubmit((values) =>
        onSubmit({
          ...values,
          firstName: values.firstName.trim(),
          lastName: values.lastName.trim(),
          email: values.email.trim(),
          jobTitle: values.jobTitle?.trim()
        })
      )}
      className="flex flex-col gap-4"
    >
      <div className="grid grid-cols-2 gap-3">
        <FormInput control={form.control} name="firstName" label={t('common.FirstName')} required />
        <FormInput control={form.control} name="lastName" label={t('common.LastName')} required />
      </div>

      <FormInput
        control={form.control}
        name="email"
        type="email"
        label={t('common.Email')}
        disabled={!isCreate}
        required
      />

      {isCreate && (
        <FormInput
          control={form.control}
          name="jobTitle"
          label={t('partner.team.columns.position')}
        />
      )}

      <FormSelect
        control={form.control}
        name="userType"
        label={t('partner.team.columns.role')}
        options={roleOptions}
        required
      />

      <DialogFooter>
        <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
          {t('common.cancel')}
        </Button>
        <Button type="submit" disabled={isSubmitting} loading={isSubmitting}>
          {t(isCreate ? 'partner.team.dialog.submitCreate' : 'common.Save')}
        </Button>
      </DialogFooter>
    </form>
  );
}
